import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../stores/appStore';

function retryCountdownLabel(remainingMs: number): string {
  if (remainingMs <= 0) {
    return 'Trying again now…';
  }

  return `Trying again in ${Math.ceil(remainingMs / 1000)}s.`;
}

export default function ReconnectBanner({ onReconnect }: { onReconnect: () => void }) {
  const reconnectStatus = useAppStore((state) => state.reconnectStatus);
  const reconnectAttempt = useAppStore((state) => state.reconnectAttempt);
  const nextReconnectAt = useAppStore((state) => state.nextReconnectAt);
  const reconnectMessage = useAppStore((state) => state.reconnectMessage);
  const connecting = useAppStore((state) => state.connecting);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (reconnectStatus !== 'retrying' || nextReconnectAt == null) {
      setNow(Date.now());
      return;
    }

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 250);

    return () => {
      window.clearInterval(timer);
    };
  }, [nextReconnectAt, reconnectStatus]);

  const remainingMs = nextReconnectAt == null ? 0 : Math.max(0, nextReconnectAt - now);

  const detail = useMemo(() => {
    if (reconnectStatus === 'success') {
      return 'You are back online.';
    }

    const parts = [] as string[];
    if (reconnectAttempt > 0) {
      parts.push(`Attempt ${reconnectAttempt}.`);
    }

    parts.push(nextReconnectAt != null ? retryCountdownLabel(remainingMs) : connecting ? 'Trying again now…' : 'Tap reconnect to try again.');
    return parts.join(' ');
  }, [connecting, nextReconnectAt, reconnectAttempt, reconnectStatus, remainingMs]);

  if (reconnectStatus === 'idle') {
    return null;
  }

  const isSuccess = reconnectStatus === 'success';

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[130] flex justify-center px-3 pt-3 sm:px-4">
      <div
        className="pointer-events-auto flex w-full max-w-2xl items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-2xl backdrop-blur-md"
        style={{
          backgroundColor: isSuccess ? 'rgba(61, 255, 168, 0.07)' : 'rgba(255, 123, 53, 0.07)',
          borderColor: isSuccess ? 'rgba(61, 255, 168, 0.2)' : 'rgba(255, 123, 53, 0.2)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <div
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{
            backgroundColor: isSuccess ? 'rgba(61, 255, 168, 0.1)' : 'rgba(255, 123, 53, 0.1)',
            color: isSuccess ? '#3DFFA8' : '#FF7B35',
          }}
        >
          {isSuccess ? (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m5 12 4 4L19 6" />
            </svg>
          ) : (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">{isSuccess ? 'Connected!' : reconnectMessage ?? 'Connection lost.'}</p>
          <p className="mt-1 text-card-foreground/90" style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem' }}>{detail}</p>
        </div>

        {!isSuccess ? (
          <button
            type="button"
            onClick={onReconnect}
            className="shrink-0 rounded-lg px-3 py-2 transition-colors"
            style={{ fontFamily: 'var(--app-ui-font)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', border: '1px solid rgba(255, 123, 53, 0.3)', background: 'rgba(255, 123, 53, 0.07)', color: '#FF7B35', transition: 'all 0.2s ease' }}
          >
            Reconnect
          </button>
        ) : null}
      </div>
    </div>
  );
}
